import React from 'react'
import PropTypes from 'prop-types'
import DetailStatComponent from '../Shared/DetailStatComponent'
import {
  coverageRows,
  daysSince,
  formatCount,
  formatPercent,
  namedCount,
  namingSegments,
  share
} from './swimLocationStats'

// One card per kind of location: how many there are, how the published
// ones are named, whether the geocoder is keeping up, and how much of
// each enrichment the directory has.
const KindSummaryComponent = ({ title, kind, isOpenWater, now }) => {
  const segments = namingSegments(kind)
  const named = namedCount(kind)
  const lastGeocodedDays = daysSince(kind.lastGeocodedAt, now)

  return (
    <div className="card h-100">
      <div className="card-header">
        <h5 className="mb-0">{title}</h5>
      </div>
      <div className="card-body">
        <div className="detail-grid">
          <DetailStatComponent
            label="Published"
            value={formatCount(kind.published)}
            source="Listed in the directory after the last weekly recompute"
          />
          <DetailStatComponent
            label="Named"
            value={formatPercent(share(named, kind.published))}
            unit={`${formatCount(named)} of ${formatCount(kind.published)}`}
          />
          <DetailStatComponent
            label="All rows"
            value={formatCount(kind.total)}
            source={'Published plus rows the recompute found that ' +
              'have not cleared the swimmer floor, or no longer do'}
          />
          <DetailStatComponent
            label="Public pages"
            value={formatCount(kind.publiclyVisible)}
            source="Published and visible to signed-out visitors"
          />
        </div>

        <div className="detail-subheading">Naming</div>
        <div
          className="swim-locations-bar"
          role="img"
          aria-label={segments
            .map(s => `${s.label} ${formatCount(s.count)}`)
            .join(', ')}
        >
          {segments.filter(s => s.count > 0).map(segment => (
            <div
              key={segment.key}
              className={`swim-locations-bar-segment ${segment.className}`}
              style={{ flexGrow: segment.count }}
              title={`${segment.label}: ${formatCount(segment.count)} ` +
                `(${formatPercent(segment.share)})`}
            />
          ))}
        </div>
        <table className="table table-sm swim-locations-legend mb-0">
          <tbody>
            {segments.map(segment => (
              <tr key={segment.key} title={segment.help}>
                <td>
                  <span
                    className={`swim-locations-swatch ${segment.className}`}
                  />
                  {segment.label}
                </td>
                <td className="text-end">{formatCount(segment.count)}</td>
                <td className="text-end text-muted">
                  {formatPercent(segment.share)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="detail-subheading">Geocoder</div>
        <div className="detail-grid">
          <DetailStatComponent
            label="Queued"
            value={formatCount(kind.pendingGeocode)}
            source="Published rows below the current geocode version"
          />
          <DetailStatComponent
            label="Geocoded, last 7 days"
            value={formatCount(kind.geocodedLastWeek)}
          />
          <DetailStatComponent
            label="Last geocoded"
            value={lastGeocodedDays === null
              ? null
              : lastGeocodedDays === 0 ? 'Today' : lastGeocodedDays}
            unit={lastGeocodedDays > 0
              ? (lastGeocodedDays === 1 ? 'day ago' : 'days ago')
              : undefined}
          />
        </div>

        <div className="detail-subheading">Coverage of published rows</div>
        <table className="table table-sm mb-0">
          <tbody>
            {coverageRows(kind, isOpenWater).map(row => (
              <tr key={row.key}>
                <td className="swim-locations-coverage-label">{row.label}</td>
                <td>
                  <div
                    className="swim-locations-meter"
                    role="meter"
                    aria-valuemin={0}
                    aria-valuemax={kind.published}
                    aria-valuenow={row.count}
                    aria-label={row.label}
                  >
                    <div
                      className="swim-locations-meter-fill"
                      style={{ width: `${(row.share || 0) * 100}%` }}
                    />
                  </div>
                </td>
                <td className="text-end">{formatPercent(row.share)}</td>
                <td className="text-end text-muted">
                  {formatCount(row.count)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const kindShape = PropTypes.shape({
  total: PropTypes.number.isRequired,
  published: PropTypes.number.isRequired,
  publiclyVisible: PropTypes.number.isRequired,
  naming: PropTypes.object.isRequired,
  pendingGeocode: PropTypes.number.isRequired,
  geocodedLastWeek: PropTypes.number.isRequired,
  lastGeocodedAt: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  withCountry: PropTypes.number.isRequired,
  withCity: PropTypes.number.isRequired,
  withPrimaryLength: PropTypes.number.isRequired,
  withCommunity: PropTypes.number.isRequired,
  withWaterTemperature: PropTypes.number.isRequired
})

KindSummaryComponent.propTypes = {
  title: PropTypes.string.isRequired,
  kind: kindShape.isRequired,
  isOpenWater: PropTypes.bool.isRequired,
  now: PropTypes.instanceOf(Date).isRequired
}

export default KindSummaryComponent
